import { Button, Typography, Space, Modal, List, Avatar, Tag, Spin, Alert, message } from "antd";
import { useState, useEffect } from "react";
import FormSetup from "../../../components/Forms/FormSetup";
import { MasterConfigs } from "../../../interfaces/entity";
import { constants } from "../../../constants/common";
import ReusableTable from "../../../components/TableComponent";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import moment from "moment";
import { formatPriceIndian } from "../../../constants/functions";
import Badge from "../../../components/Badge";
import { putData, postData } from "../../../actions/developer";
import { useDispatch } from "react-redux";
import { getData } from "../../../actions/apiCall";
import dayjs from "dayjs";
import InterviewScheduleModal, { TimelineEvent } from "../Calender/InterviewScheduleModal";
import axios from "axios";
import { getAccess } from "../../../actions/auth";

const { Title } = Typography;

/* =========================
   FIGMA TIME SLOT LOGIC
========================= */
const generateTimeSlots = (
  start = "10:00 AM",
  end = "06:00 PM",
  step = 60
) => {
  const slots: { label: string; value: string }[] = [];
  let current = moment(start, "hh:mm A");
  const endTime = moment(end, "hh:mm A");

  while (current <= endTime) {
    slots.push({
      label: current.format("hh:mm A"),
      value: current.format("hh:mm A"),
    });
    current.add(step, "minutes");
  }

  return slots;
};

interface DevelopersProps {
  refType: string;
  refId: string;
  highlighted: boolean;
}

const Developers: React.FC<DevelopersProps> = ({
  refType,
  refId,
  highlighted,
}) => {
  const [open, setOpen] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [openViewInterviews, setOpenViewInterviews] = useState(false);
  const [openInterviewScheduleModal, setOpenInterviewScheduleModal] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState<any>(null);
  const [selectedInterviewEvent, setSelectedInterviewEvent] = useState<TimelineEvent | null>(null);
  const [fromTime, setFromTime] = useState<string | null>(null);
  const [interviews, setInterviews] = useState<any[]>([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);

  const dispatch = useDispatch();

  const handleScheduleInterview = (record: any) => {
    setSelectedDeveloper(record);
    setOpenSchedule(true);
  };

  const handleViewInterviews = async (record: any) => {
    setSelectedDeveloper(record);
    setOpenViewInterviews(true);
    await fetchDeveloperInterviews(record._id);
  };

  const handleOpenInterviewScheduleModal = async (record: any, interviewId?: string) => {
    try {
      // If interviewId is provided, fetch that specific interview
      if (interviewId) {
        axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
        const response = await dispatch(getData(`/contract_interview/${interviewId}`));

        if (response?.code === 200 && response?.data) {
          const interviewData = response.data;
          const interviewDate = interviewData.interview_datetime
            ? dayjs(interviewData.interview_datetime)
            : dayjs();
          const duration = interviewData.duration || 60;
          const endTime = interviewDate.clone().add(duration, 'minute');

          const timelineEvent: TimelineEvent = {
            id: interviewData._id,
            interviewId: interviewData._id,
            time: interviewDate.format('hh:mm A'),
            duration: `${duration}min`,
            title: interviewData.interviewTitle || 'Interview',
            status: (interviewData.interviewStatus?.toLowerCase() as 'selected' | 'rejected' | 'upcoming') || 'upcoming',
            candidate: {
              name: interviewData.developer
                ? `${interviewData.developer.firstName || ''} ${interviewData.developer.lastName || ''}`.trim()
                : constants.GET_FULL_NAME(record),
              email: interviewData.developer?.email || record?.email || '',
              avatar: interviewData.developer?.avatar ? `${process.env.FILE_URL}${interviewData.developer.avatar}` : undefined,
              _id: interviewData.developer?._id || record?._id
            },
            interviewer: {
              name: interviewData.interviewOwner
                ? `${interviewData.interviewOwner.firstName || ''} ${interviewData.interviewOwner.lastName || ''}`.trim()
                : 'N/A',
              email: interviewData.interviewOwner?.workEmail || '',
              avatar: interviewData.interviewOwner?.avatar ? `${process.env.FILE_URL}${interviewData.interviewOwner.avatar}` : undefined,
              _id: interviewData.interviewOwner?._id
            },
            role: interviewData.developer?.role ||
              (typeof interviewData.developer?.jobTitle === 'object' ? interviewData.developer.jobTitle?.name : interviewData.developer?.jobTitle) ||
              record?.jobTitle?.name ||
              interviewData.job_description ||
              'N/A',
            dateStr: interviewDate.format('DD MMMM YYYY'),
            meetingLink: interviewData.interview_link || '',
            interviewLevel: interviewData.interview_type || interviewData.interviewTitle || '',
            developerId: interviewData.developer?._id || record?._id,
            startTime: interviewDate.format('hh:mm A'),
            endTime: endTime.format('hh:mm A')
          };

          setSelectedInterviewEvent(timelineEvent);
          setOpenInterviewScheduleModal(true);
        }
      } else {
        // If no interviewId, fetch interviews for this developer first
        setLoadingInterviews(true);
        try {
          const response = await dispatch(getData(`/calendar/interviews/developer/${record._id}`));
          if (response?.code === 200 && response?.data && response.data.interviews && response.data.interviews.length > 0) {
            // Get the first interview ID and fetch its details
            const firstInterview = response.data.interviews[0];
            if (firstInterview.interviewId || firstInterview._id) {
              await handleOpenInterviewScheduleModal(record, firstInterview.interviewId || firstInterview._id);
            } else {
              // If no interview ID, open schedule modal
              handleScheduleInterview(record);
            }
          } else {
            // If no interviews, open schedule modal
            handleScheduleInterview(record);
          }
        } catch (error) {
          console.error('Error fetching developer interviews:', error);
          handleScheduleInterview(record);
        } finally {
          setLoadingInterviews(false);
        }
      }
    } catch (error) {
      console.error('Error fetching interview details:', error);
      // Fallback to schedule interview if error
      handleScheduleInterview(record);
    }
  };

  const fetchDeveloperInterviews = async (developerId: string) => {
    if (!developerId) return;

    setLoadingInterviews(true);
    try {
      const response = await dispatch(getData(`/calendar/interviews/developer/${developerId}`));
      if (response?.code === 200 && response?.data) {
        setInterviews(response.data.interviews || []);
      } else {
        setInterviews([]);
      }
    } catch (error) {
      console.error('Error fetching developer interviews:', error);
      setInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  /* =========================
     ADD DEVELOPERS CONFIG
  ========================= */
  const config: MasterConfigs = {
    api: () => `/deals/developer/list`,
    queryParams: { id: refId },
    title: "Developers",
    modalTitle: () => "Add Developer",

    getColumns: () => [
      {
        title: "Developer",
        dataIndex: "developer",
        key: "developer",
        width: 200,
        fixed: "left",
        render: (_, record) => (
          <SmallProfileCard
            name={constants.GET_FULL_NAME(record)}
            subTitle={record?.jobTitle?.name || ""}
          />
        ),
      },
      {
        title: "Experience",
        width: 150,
        align: "center",
        dataIndex: "experience",
        render: (text) => (text ? `${text} Years` : "N/A"),
      },
      {
        title: "Notice Period",
        width: 150,
        align: "center",
        dataIndex: "noticePeriodDays",
        render: (text) => (text ? `${text} Days` : "N/A"),
      },
      {
        title: "LWD",
        width: 150,
        align: "center",
        dataIndex: "lastWorkingDate",
        render: (text) =>
          text ? moment(text).format("LL") : "N/A",
      },
      {
        title: "Priority",
        width: 150,
        align: "center",
        dataIndex: "priority",
        render: (text) => {
          const color =
            text === "High" ? "red" : text === "Low" ? "green" : "yellow";
          return <Badge color={color} text={text || "N/A"} />;
        },
      },
      {
        title: "Available",
        width: 200,
        dataIndex: "developerAvailability",
        render: (text) =>
          constants.DEVELOPER_AVAILABILITY.find(
            (i) => i.value === text
          )?.name || "N/A",
      },
      {
        title: "Budget Range",
        width: 200,
        dataIndex: "budgetRange",
        render: (text) => (text ? formatPriceIndian(text) : "N/A"),
      },
      {
        title: "Schedule Interview",
        width: 200,
        align: "center",
        render: (_, record) =>
          <Button
            type="default"
            className="text-blue-500 border-blue-500"
            onClick={() => message.warning('Schedule interview from the calendar tab')}
          >
            Schedule +
          </Button>
      },
      {
        title: "Vendor",
        width: 200,
        dataIndex: "vendorPartner",
        render: (_, record) =>
          record?.vendorPartner?.vendorName ? (
            <Space direction="vertical">
              {record.vendorPartner.vendorName}
              {record?.vendorBudget && (
                <span className="text-xs text-gray-500">
                  Budget: {formatPriceIndian(record.vendorBudget)}
                </span>
              )}
            </Space>
          ) : (
            "Own Profile"
          ),
      },
    ],
  };

  /* =========================
     ADD DEVELOPER CONFIG
  ========================= */
  const addDeveloperConfig: MasterConfigs = {
    api: () => `/deals`,
    modalTitle: () => "Add Developer to Deal",
    modalProps: { width: 600 },

    getFormColumns: () => [
      {
        label: "Select Developer",
        name: "developer",
        type: "select",
        isCombo: true,
        span: 24,
        rules: [{ required: true, message: "Please select a developer" }],
        selectProps: {
          showSearch: true,
          filterOption: false,
          optionLabelProp: "label",
        },
      },
    ],

    combo: [
      {
        api: "/bench-developers",
        name: "developer",
        idKey: "_id",
        render: (text: any) => (
          <SmallProfileCard
            name={constants.GET_FULL_NAME(text)}
            subTitle={text?.jobTitle?.name || ""}
          />
        ),
      },
    ],

    onSubmit: async (id, values) => {
      const response = await dispatch(
        putData(`/deals/${refId}`, {
          developer: values.developer,
        })
      );
      if (response?.code === 200 || response?._id) {
        // Refresh the table by dispatching refresh action
        dispatch({
          type: 'REFRESH_TABLE',
          refreshTable: true
        });
        setOpen(false);
      }
      return response;
    },
  };

  /* =========================
     SCHEDULE INTERVIEW CONFIG
  ========================= */
  const scheduleConfig: MasterConfigs = {
    api: () => `/interviews/schedule`,
    modalTitle: () => "Schedule Interview",
    modalProps: { width: 700 },

    getFormColumns: () => [
      {
        label: "Scheduler Owner",
        name: "schedulerOwner",
        type: "select",
        isCombo: true,
        span: 12,
        rules: [{ required: true }],
      },
      {
        label: "Select Date",
        name: "interviewDate",
        type: "date",
        span: 12,
        rules: [{ required: true }],
      },

      /* ---------- FROM SLOT ---------- */
      {
        label: "Select Slots",
        name: "startTime",
        type: "select",
        span: 6,
        placeholder: "From",
        options: generateTimeSlots(),
        rules: [{ required: true }],
        rest: {
          onChange: (value: string, form: any) => {
            setFromTime(value);
            form.setFieldsValue({ endTime: null, duration: null });
          },
        },
      },

      /* ---------- TO SLOT ---------- */
      {
        label: " ",
        name: "endTime",
        type: "select",
        span: 6,
        placeholder: "To",
        rules: [{ required: true }],
        options: fromTime
          ? generateTimeSlots().filter((slot) =>
            moment(slot.value, "hh:mm A").isAfter(
              moment(fromTime, "hh:mm A")
            )
          )
          : [],
        rest: {
          onChange: (value: string, form: any) => {
            const start = moment(
              form.getFieldValue("startTime"),
              "hh:mm A"
            );
            const end = moment(value, "hh:mm A");

            const duration = moment
              .duration(end.diff(start))
              .asMinutes();

            form.setFieldsValue({
              duration: `${duration} mins`,
            });
          },
        },
      },

      {
        label: "Duration",
        name: "duration",
        type: "text",
        disabled: true,
        span: 12,
        placeholder: "(Auto calculate)",
      },
      {
        label: "Interview Link",
        name: "interviewLink",
        type: "text",
        span: 12,
      },
      {
        label: "Round",
        name: "round",
        type: "select",
        span: 12,
        rules: [{ required: true }],
        options: [
          { label: "Technical Round 1", value: "L1" },
          { label: "Technical Round 2", value: "L2" },
          { label: "HR Round", value: "HR" },
        ],
      },
      {
        label: "Description",
        name: "description",
        type: "textarea",
        span: 24,
        rest: { rows: 4 },
      },
    ],

    combo: [
      {
        api: `/users/owners`,
        name: "schedulerOwner",
        idKey: "_id",
        render: (text: any) => text?.name,
      },
    ],

    onSubmit: async (id, value) => {
      const response = await dispatch(
        postData(`/interviews`, {
          ...value,
          developerId: selectedDeveloper?._id,
          refId: id,
        })
      );
      if (response?.code === 200 || response?._id) {
        // Refresh the table after successful interview scheduling
        dispatch({
          type: 'REFRESH_TABLE',
          refreshTable: true
        });
        setOpenSchedule(false);
        setFromTime(null);
      }
      return response;
    },
  };

  return (
    <div>
      <div className="flex justify-between items-center py-2 px-4 border-b">
        <Title level={5} className={highlighted ? "bg-yellow-200 px-1" : ""}>
          Developers
        </Title>
        <Button type="text" onClick={() => setOpen(true)}>
          Add Developers
        </Button>
      </div>

      <FormSetup
        isModal
        open={open}
        config={addDeveloperConfig}
        id={refId}
        onClose={() => {
          setOpen(false);
        }}
      />

      <FormSetup
        isModal
        open={openSchedule}
        config={scheduleConfig}
        id={refId}
        onClose={() => {
          setOpenSchedule(false);
          setFromTime(null);
        }}
      />

      <Modal
        open={openViewInterviews}
        onCancel={() => {
          setOpenViewInterviews(false);
          setSelectedDeveloper(null);
          setInterviews([]);
        }}
        title={
          selectedDeveloper ? (
            <div className="flex items-center gap-3">
              <Avatar className="bg-blue-100 text-blue-600 font-bold">
                {constants.GET_FULL_NAME(selectedDeveloper)?.charAt(0) || 'D'}
              </Avatar>
              <div>
                <div className="font-bold text-lg">
                  {constants.GET_FULL_NAME(selectedDeveloper) || 'Developer'}
                </div>
                <div className="text-xs text-gray-500">
                  {selectedDeveloper?.jobTitle?.name || ''}
                </div>
              </div>
            </div>
          ) : 'Developer Interviews'
        }
        footer={null}
        width={700}
        centered
      >
        {loadingInterviews ? (
          <div className="flex justify-center items-center py-8">
            <Spin size="large" />
          </div>
        ) : interviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No interviews scheduled for this developer
          </div>
        ) : (
          <List
            dataSource={interviews}
            renderItem={(interview: any) => {
              const interviewDate = interview.date ? dayjs(interview.date) : null;
              const timeStr = interview.time || 'N/A';

              return (
                <List.Item
                  className="hover:bg-slate-50 rounded-lg px-4 py-3 transition-colors cursor-pointer"
                  onClick={() => {
                    if (interview.interviewId || interview._id) {
                      handleOpenInterviewScheduleModal(selectedDeveloper, interview.interviewId || interview._id);
                    }
                  }}
                >
                  <div className="flex justify-between items-center w-full">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-xs text-gray-500 uppercase font-semibold">
                          {interviewDate ? interviewDate.format('MMM') : 'N/A'}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">
                          {interviewDate ? interviewDate.format('DD') : '--'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {interviewDate ? interviewDate.format('YYYY') : ''}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">
                            {interview.interviewTitle || interview.interview_type || 'Interview'}
                          </span>
                          {interview.interviewStatus && (
                            <Tag
                              color={
                                interview.interviewStatus === 'Selected'
                                  ? 'green'
                                  : interview.interviewStatus === 'Rejected'
                                    ? 'red'
                                    : interview.interviewStatus === 'Inprogress'
                                      ? 'blue'
                                      : 'orange'
                              }
                            >
                              {interview.interviewStatus}
                            </Tag>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          {timeStr !== 'N/A' && (
                            <div>Time: {timeStr}</div>
                          )}
                          {interview.duration && (
                            <div>Duration: {interview.duration}</div>
                          )}
                          {interview.interviewerName && (
                            <div>Interviewer: {interview.interviewerName}</div>
                          )}
                          {interview.accountName && (
                            <div>Account: {interview.accountName}</div>
                          )}
                          {interview.dealName && (
                            <div>Deal: {interview.dealName}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>

      <InterviewScheduleModal
        visible={openInterviewScheduleModal}
        onClose={() => {
          setOpenInterviewScheduleModal(false);
          setSelectedInterviewEvent(null);
        }}
        event={selectedInterviewEvent || undefined}
        onFeedbackAdded={() => {
          dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true
          });
        }}
      />

      <div className="p-4">
        <ReusableTable config={config} />
      </div>
    </div>
  );
};

export default Developers;
